import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Package, ShoppingCart, BarChart3, MessageSquare,
  LogOut, AlertCircle, ChevronRight, Search, Users, Mail, ShoppingBag, Tag, Bell, Star, CreditCard
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { formatFCFA } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DashSidebar({ active, setActive, user, onLogout }) {
  const NAV = [
    { id: 'inventory', label: 'Inventaire', icon: Package },
    { id: 'orders', label: 'Commandes', icon: ShoppingCart },
    { id: 'payments', label: 'Paiements', icon: CreditCard },
    { id: 'abandoned', label: 'Paniers abandonnés', icon: ShoppingBag },
    { id: 'promos', label: 'Codes Promo', icon: Tag },
    { id: 'reviews', label: 'Avis', icon: Star },
    { id: 'customers', label: 'Clients', icon: Users },
    { id: 'emails', label: 'Emails', icon: Mail },
    { id: 'analytics', label: 'Analytique', icon: BarChart3 },
    { id: 'inbox', label: 'Messages', icon: MessageSquare },
  ];

  return (
    <div className="dash-sidebar w-64 flex-shrink-0 hidden lg:flex flex-col h-screen fixed left-0 top-0 py-8 px-4">
      <div className="mb-12 px-4">
        <h1 className="font-serif text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Balmart
        </h1>
        <p className="text-white/30 text-xs mt-1 uppercase tracking-widest">Gestion</p>
      </div>

      <nav className="flex-1 space-y-1">
        {NAV.map(item => (
          <button
            key={item.id}
            data-testid={`dash-nav-${item.id}`}
            onClick={() => setActive(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors rounded ${
              active === item.id
                ? 'bg-white/10 text-white'
                : 'text-white/40 hover:text-white/70 hover:bg-white/5'
            }`}
          >
            <item.icon size={18} strokeWidth={1.5} />
            {item.label}
          </button>
        ))}
      </nav>

      <div className="px-4 border-t border-white/10 pt-6">
        {user && (
          <div className="flex items-center gap-3 mb-4">
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{user.name}</p>
              <p className="text-xs text-white/30 truncate">{user.email}</p>
            </div>
          </div>
        )}
        <button data-testid="dash-logout-btn" onClick={onLogout} className="flex items-center gap-2 text-xs text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest">
          <LogOut size={14} /> D&eacute;connexion
        </button>
      </div>
    </div>
  );
}


function PaymentsMonitoringPanel() {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/payments/monitoring`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const act = async (orderId, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setActioning(a => ({ ...a, [orderId + action]: true }));
    try {
      await axios.post(`${API}/admin/payments/fix`, { order_id: orderId, action });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur');
    } finally {
      setActioning(a => ({ ...a, [orderId + action]: false }));
    }
  };

  const formatFCFA = (n) => `${Number(n || 0).toLocaleString('fr-FR')} FCFA`;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (loading || !data) {
    return <div className="text-white/60">Chargement…</div>;
  }

  const { stats, stuck_orders, failed_orders, missing_loyalty_orders } = data;

  const StatCard = ({ label, value, accent }) => (
    <div className="border border-white/10 p-5 bg-white/[0.02]">
      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{label}</p>
      <p className="text-3xl font-light" style={accent ? { color: accent } : {}}>{value}</p>
    </div>
  );

  const OrderRow = ({ order, actions }) => (
    <div data-testid={`payment-row-${order.order_id}`} className="border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-3 bg-white/[0.02]">
      <div className="flex-1 min-w-0">
        <p className="font-mono text-sm text-white">#{order.order_id?.replace('ord_', '').toUpperCase()}</p>
        <p className="text-xs text-white/60 mt-1 truncate">{order.customer_name} · {order.customer_email} · {order.customer_phone}</p>
        <p className="text-xs text-white/40 mt-1">
          {fmtDate(order.created_at)} · <span className="text-white/70">{formatFCFA(order.total)}</span>
          {order.payment_operator && <span> · {order.payment_operator.toUpperCase()}</span>}
          {order.payment_status && <span className="ml-2 px-2 py-0.5" style={{ backgroundColor: order.payment_status === 'paid' ? 'rgba(34,197,94,0.2)' : order.payment_status === 'failed' ? 'rgba(239,68,68,0.2)' : 'rgba(250,204,21,0.2)', color: order.payment_status === 'paid' ? '#22c55e' : order.payment_status === 'failed' ? '#ef4444' : '#facc15' }}>{order.payment_status}</span>}
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {actions}
      </div>
    </div>
  );

  return (
    <div data-testid="payments-monitoring" className="space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-light text-white mb-1">Monitoring Paiements</h2>
          <p className="text-sm text-white/50">Commandes bloquées, paiements échoués, actions manuelles.</p>
        </div>
        <button data-testid="refresh-payments-btn" onClick={load} className="text-xs uppercase tracking-[0.2em] font-bold px-4 py-2 border border-white/20 text-white hover:bg-white hover:text-black transition-colors">
          Rafraîchir
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total commandes" value={stats.total_orders} />
        <StatCard label="Payées" value={stats.total_paid} accent="#22c55e" />
        <StatCard label="En attente" value={stats.total_awaiting} accent="#facc15" />
        <StatCard label="Échouées" value={stats.total_failed} accent="#ef4444" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard label="Taux de conversion" value={`${stats.conversion_rate}%`} accent="#B80000" />
        <StatCard label="Commandes bloquées" value={stats.stuck_count} accent={stats.stuck_count > 0 ? '#facc15' : undefined} />
      </div>

      <div>
        <h3 className="text-lg font-light text-white mb-3">
          <span className="inline-block mr-2" style={{ color: '#facc15' }}>●</span>
          Commandes bloquées en paiement ({stuck_orders.length})
        </h3>
        <p className="text-xs text-white/50 mb-4">Statut <code>awaiting_payment</code> depuis plus de 30 min. Vérifie le statut du paiement avant de marquer comme payé.</p>
        {stuck_orders.length === 0 ? (
          <div className="text-xs text-white/40 border border-white/5 p-4">Aucune commande bloquée ✅</div>
        ) : (
          <div className="space-y-2">
            {stuck_orders.map(o => (
              <OrderRow key={o.order_id} order={o} actions={
                <>
                  <button
                    data-testid={`mark-paid-${o.order_id}`}
                    disabled={actioning[o.order_id + 'mark_paid']}
                    onClick={() => act(o.order_id, 'mark_paid', "Marquer cette commande comme PAYÉE ? (envoie l'email de confirmation + crédite les points)")}
                    className="text-[11px] uppercase tracking-wider px-3 py-2 font-bold border border-green-500/30 text-green-400 hover:bg-green-500 hover:text-black transition-colors disabled:opacity-50"
                  >Marquer payée</button>
                  <button
                    data-testid={`cancel-${o.order_id}`}
                    disabled={actioning[o.order_id + 'cancel']}
                    onClick={() => act(o.order_id, 'cancel', 'Annuler définitivement cette commande ?')}
                    className="text-[11px] uppercase tracking-wider px-3 py-2 font-bold border border-red-500/30 text-red-400 hover:bg-red-500 hover:text-black transition-colors disabled:opacity-50"
                  >Annuler</button>
                </>
              } />
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-light text-white mb-3">
          <span className="inline-block mr-2" style={{ color: '#ef4444' }}>●</span>
          Paiements échoués ({failed_orders.length})
        </h3>
        {failed_orders.length === 0 ? (
          <div className="text-xs text-white/40 border border-white/5 p-4">Aucun paiement échoué récemment ✅</div>
        ) : (
          <div className="space-y-2">
            {failed_orders.slice(0, 20).map(o => (
              <OrderRow key={o.order_id} order={o} actions={
                <button
                  data-testid={`retry-paid-${o.order_id}`}
                  disabled={actioning[o.order_id + 'mark_paid']}
                  onClick={() => act(o.order_id, 'mark_paid', 'Réactiver cette commande comme PAYÉE ?')}
                  className="text-[11px] uppercase tracking-wider px-3 py-2 font-bold border border-green-500/30 text-green-400 hover:bg-green-500 hover:text-black transition-colors disabled:opacity-50"
                >Forcer payée</button>
              } />
            ))}
          </div>
        )}
      </div>

      {missing_loyalty_orders.length > 0 && (
        <div>
          <h3 className="text-lg font-light text-white mb-3">
            <span className="inline-block mr-2" style={{ color: '#B80000' }}>●</span>
            Points fidélité non crédités ({missing_loyalty_orders.length})
          </h3>
          <p className="text-xs text-white/50 mb-4">Commandes payées (clients connectés) sans points attribués. Bug rare — tu peux créditer manuellement.</p>
          <div className="space-y-2">
            {missing_loyalty_orders.slice(0, 20).map(o => (
              <OrderRow key={o.order_id} order={o} actions={
                <button
                  data-testid={`credit-loyalty-${o.order_id}`}
                  disabled={actioning[o.order_id + 'credit_loyalty']}
                  onClick={() => act(o.order_id, 'credit_loyalty', `Créditer ${Number(o.total || 0).toLocaleString('fr-FR')} points au client ?`)}
                  className="text-[11px] uppercase tracking-wider px-3 py-2 font-bold border" style={{ borderColor: 'rgba(184,0,0,0.4)', color: '#B80000' }}
                >Créditer points</button>
              } />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function PromosPanel() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount_percent: 10, usage_limit: 100, valid_days: 30, customer_email: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/promos`, { withCredentials: true });
      setPromos(r.data || []);
    } catch {
      setPromos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createPromo = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/admin/promos`, form, { withCredentials: true });
      setForm({ code: '', discount_percent: 10, usage_limit: 100, valid_days: 30, customer_email: '', description: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (code) => {
    await axios.patch(`${API}/admin/promos/${code}`, {}, { withCredentials: true });
    await load();
  };

  const deletePromo = async (code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    await axios.delete(`${API}/admin/promos/${code}`, { withCredentials: true });
    await load();
  };

  const [broadcastCode, setBroadcastCode] = useState(null);
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastIntro, setBroadcastIntro] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const openBroadcast = (code) => {
    setBroadcastCode(code);
    setBroadcastSubject('');
    setBroadcastIntro('');
    setBroadcastResult(null);
  };

  const sendBroadcast = async () => {
    setBroadcastSending(true);
    try {
      const r = await axios.post(
        `${API}/admin/promos/${broadcastCode}/broadcast`,
        { subject: broadcastSubject, intro_message: broadcastIntro, recipient_type: 'all' },
        { withCredentials: true }
      );
      setBroadcastResult(r.data);
      await load();
    } catch (err) {
      setBroadcastResult({ error: err?.response?.data?.detail || 'Erreur lors de l\'envoi' });
    } finally {
      setBroadcastSending(false);
    }
  };

  const stats = {
    total: promos.length,
    active: promos.filter(p => p.active).length,
    totalUses: promos.reduce((s, p) => s + (p.times_used || 0), 0),
    revenueImpact: promos.reduce((s, p) => s + (p.revenue_generated || 0), 0),
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Codes Promo</h2>
          <p className="text-xs text-white/30 mt-1">Créez et suivez vos campagnes de réduction</p>
        </div>
        <button
          data-testid="new-promo-btn"
          onClick={() => setShowForm(!showForm)}
          className="px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold"
          style={{ backgroundColor: '#B80000', color: '#fff' }}
        >
          {showForm ? 'Annuler' : 'Nouveau Code'}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Total</p>
          <p className="text-3xl text-white font-light">{stats.total}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Actifs</p>
          <p className="text-3xl text-green-400 font-light">{stats.active}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Utilisations</p>
          <p className="text-3xl font-light" style={{ color: '#B80000' }}>{stats.totalUses}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">CA généré</p>
          <p className="text-xl text-white font-light">{Math.round(stats.revenueImpact).toLocaleString('fr-FR')} FCFA</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={createPromo} className="mb-8 p-6 bg-white/5 border border-white/10 space-y-4">
          <h3 className="text-sm text-white font-semibold uppercase tracking-widest mb-4">Nouveau Code Promo</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Code (optionnel)</label>
              <input
                data-testid="promo-form-code"
                type="text"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="Auto-généré si vide"
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white uppercase tracking-wider"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Remise (%) *</label>
              <input
                data-testid="promo-form-discount"
                type="number" min="1" max="100" required
                value={form.discount_percent}
                onChange={e => setForm({ ...form, discount_percent: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Usages max</label>
              <input
                type="number" min="1" required
                value={form.usage_limit}
                onChange={e => setForm({ ...form, usage_limit: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Valide (jours)</label>
              <input
                type="number" min="1" required
                value={form.valid_days}
                onChange={e => setForm({ ...form, valid_days: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Email client (réservé)</label>
              <input
                type="email"
                value={form.customer_email}
                onChange={e => setForm({ ...form, customer_email: e.target.value })}
                placeholder="Laissez vide pour un code public"
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Description interne</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Ex: Black Friday 2026"
                className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
              />
            </div>
          </div>
          {error && <p className="text-xs" style={{ color: '#B80000' }}>{error}</p>}
          <button
            data-testid="submit-promo-btn"
            type="submit"
            disabled={submitting}
            className="px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold"
            style={{ backgroundColor: '#B80000', color: '#fff', opacity: submitting ? 0.5 : 1 }}
          >
            {submitting ? 'Création...' : 'Créer le Code'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-white/30 text-sm">Chargement...</p>
      ) : promos.length === 0 ? (
        <div className="text-center py-16 border border-white/10">
          <Tag size={32} className="mx-auto mb-4 text-white/20" />
          <p className="text-white/40 text-sm">Aucun code promo</p>
        </div>
      ) : (
        <div className="space-y-2">
          {promos.map(promo => {
            const expired = new Date(promo.expires_at) < new Date();
            const exhausted = (promo.times_used || 0) >= (promo.usage_limit || 1);
            const usage_pct = Math.min(100, Math.round(((promo.times_used || 0) / (promo.usage_limit || 1)) * 100));
            return (
              <div key={promo.code} data-testid={`promo-row-${promo.code}`} className="p-4 bg-white/5 border border-white/10">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-lg text-white font-semibold tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>
                        {promo.code}
                      </p>
                      <span className="text-xs px-2 py-0.5 font-bold" style={{ backgroundColor: '#B80000', color: '#fff' }}>
                        -{promo.discount_percent}%
                      </span>
                      {!promo.active && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-white/10 text-white/60">Désactivé</span>}
                      {expired && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-yellow-500/20 text-yellow-400">Expiré</span>}
                      {exhausted && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-red-500/20 text-red-400">Épuisé</span>}
                      {promo.source === 'cart_recovery' && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-blue-500/20 text-blue-400">Auto</span>}
                    </div>
                    {promo.description && <p className="text-xs text-white/50 mt-1">{promo.description}</p>}
                    {promo.customer_email && <p className="text-[10px] text-white/30 mt-1">Réservé à : {promo.customer_email}</p>}
                    <div className="flex items-center gap-4 mt-3 text-[10px] text-white/40 uppercase tracking-widest flex-wrap">
                      <span>{promo.times_used || 0}/{promo.usage_limit} utilisé{(promo.times_used || 0) > 1 ? 's' : ''}</span>
                      <span>·</span>
                      <span>Expire le {new Date(promo.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      {promo.revenue_generated > 0 && (
                        <>
                          <span>·</span>
                          <span className="text-green-400">
                            {Math.round(promo.revenue_generated).toLocaleString('fr-FR')} FCFA générés
                          </span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 h-1 bg-white/5 overflow-hidden w-full max-w-xs">
                      <div className="h-full" style={{ width: `${usage_pct}%`, backgroundColor: '#B80000' }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      data-testid={`broadcast-promo-${promo.code}`}
                      onClick={() => openBroadcast(promo.code)}
                      disabled={!promo.active}
                      className="text-[10px] uppercase tracking-widest px-3 py-1.5 font-bold transition-colors"
                      style={{ backgroundColor: '#B80000', color: '#fff', opacity: promo.active ? 1 : 0.3 }}
                    >
                      Envoyer aux clients
                    </button>
                    <button
                      data-testid={`toggle-promo-${promo.code}`}
                      onClick={() => toggleActive(promo.code)}
                      className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-white/20 text-white/60 hover:text-white hover:border-white transition-colors"
                    >
                      {promo.active ? 'Désactiver' : 'Activer'}
                    </button>
                    <button
                      data-testid={`delete-promo-${promo.code}`}
                      onClick={() => deletePromo(promo.code)}
                      className="text-[10px] uppercase tracking-widest px-3 py-1.5 text-white/30 hover:text-red-400 transition-colors"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Broadcast modal */}
      {broadcastCode && (
        <div data-testid="broadcast-modal" className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !broadcastSending && setBroadcastCode(null)}>
          <div className="bg-[#0A0A0A] border border-white/10 max-w-lg w-full p-8" onClick={e => e.stopPropagation()}>
            {broadcastResult ? (
              <div className="text-center py-4">
                {broadcastResult.error ? (
                  <>
                    <p className="text-red-400 text-sm mb-6">{broadcastResult.error}</p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto mb-4 w-14 h-14 flex items-center justify-center rounded-full" style={{ backgroundColor: '#B80000' }}>
                      <Mail size={24} className="text-white" />
                    </div>
                    <h3 className="font-serif text-2xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Campagne envoyée</h3>
                    <p className="text-sm text-white/60 mb-6">
                      <span className="text-green-400 font-semibold">{broadcastResult.delivered}</span> emails livrés
                      {broadcastResult.failed > 0 && <> · <span className="text-red-400">{broadcastResult.failed} échecs</span></>}
                      <br />sur {broadcastResult.recipient_count} destinataires
                    </p>
                  </>
                )}
                <button
                  onClick={() => setBroadcastCode(null)}
                  className="px-6 py-2 text-xs uppercase tracking-[0.2em] font-bold text-white/70 border border-white/20 hover:text-white hover:border-white"
                >
                  Fermer
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Envoyer <span style={{ color: '#B80000' }}>{broadcastCode}</span> aux clients
                </h3>
                <p className="text-xs text-white/40 mb-6">L'email sera envoyé à tous les clients enregistrés</p>

                <div className="space-y-4">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Objet (optionnel)</label>
                    <input
                      type="text"
                      value={broadcastSubject}
                      onChange={e => setBroadcastSubject(e.target.value)}
                      placeholder="Auto-généré si vide"
                      className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Message d'introduction (optionnel)</label>
                    <textarea
                      value={broadcastIntro}
                      onChange={e => setBroadcastIntro(e.target.value)}
                      rows="3"
                      placeholder="Un message personnalisé avant le code promo..."
                      className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    data-testid="confirm-broadcast"
                    onClick={sendBroadcast}
                    disabled={broadcastSending}
                    className="flex-1 py-3 text-xs uppercase tracking-[0.2em] font-bold"
                    style={{ backgroundColor: '#B80000', color: '#fff', opacity: broadcastSending ? 0.5 : 1 }}
                  >
                    {broadcastSending ? 'Envoi en cours...' : 'Envoyer à tous les clients'}
                  </button>
                  <button
                    onClick={() => setBroadcastCode(null)}
                    disabled={broadcastSending}
                    className="px-5 py-3 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white"
                  >
                    Annuler
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function AbandonedCartsPanel() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/abandoned-carts`, { withCredentials: true });
      setCarts(r.data || []);
    } catch {
      setCarts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerRecovery = async () => {
    setTriggering(true);
    try {
      const r = await axios.post(`${API}/admin/abandoned-carts/trigger`, {}, { withCredentials: true });
      setLastRun(r.data);
      await load();
    } finally {
      setTriggering(false);
    }
  };

  const total = carts.length;
  const awaitingPayment = carts.filter(c => c.payment_status === 'awaiting_payment').length;
  const recovered = carts.filter(c => c.recovery_email_sent_at).length;
  const potentialRevenue = carts.reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Paniers Abandonnés</h2>
          <p className="text-xs text-white/30 mt-1">Emails de relance envoyés automatiquement 24h après un paiement non finalisé</p>
        </div>
        <button
          data-testid="trigger-recovery-btn"
          onClick={triggerRecovery}
          disabled={triggering}
          className="px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-opacity"
          style={{ backgroundColor: '#B80000', color: '#fff', opacity: triggering ? 0.5 : 1 }}
        >
          {triggering ? 'Envoi...' : 'Lancer les relances'}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Total</p>
          <p className="text-3xl text-white font-light">{total}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">En attente</p>
          <p className="text-3xl font-light" style={{ color: '#B80000' }}>{awaitingPayment}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Relancés</p>
          <p className="text-3xl text-green-400 font-light">{recovered}</p>
        </div>
        <div className="p-5 bg-white/5 border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">Potentiel</p>
          <p className="text-xl text-white font-light">{Math.round(potentialRevenue).toLocaleString('fr-FR')} FCFA</p>
        </div>
      </div>

      {lastRun && (
        <div className="mb-6 p-4 border border-green-500/30 bg-green-500/5 text-xs text-green-400">
          Dernière exécution: {lastRun.checked} vérifiés · {lastRun.sent} emails envoyés
        </div>
      )}

      {loading ? (
        <p className="text-white/30 text-sm">Chargement...</p>
      ) : carts.length === 0 ? (
        <div className="text-center py-16 border border-white/10">
          <ShoppingBag size={32} className="mx-auto mb-4 text-white/20" />
          <p className="text-white/40 text-sm">Aucun panier abandonné</p>
        </div>
      ) : (
        <div className="space-y-2">
          {carts.map(cart => (
            <div key={cart.order_id} data-testid={`abandoned-${cart.order_id}`} className="p-4 bg-white/5 border border-white/10 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-semibold truncate">
                  {cart.customer_name || 'Client anonyme'}
                  <span className="text-white/30 ml-2">· {cart.customer_email}</span>
                </p>
                <p className="text-xs text-white/30 mt-1">
                  #{cart.order_id.replace('ord_', '').toUpperCase()} ·
                  {cart.items?.length} article{cart.items?.length > 1 ? 's' : ''} ·
                  Créé le {new Date(cart.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-sm text-white font-light">{Math.round(cart.total || 0).toLocaleString('fr-FR')} FCFA</span>
                {cart.recovery_email_sent_at ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-green-500/20 text-green-400">Relancé</span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-yellow-500/20 text-yellow-400">En attente</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function InventoryPanel() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ price: '', stock: '' });
  const [adding, setAdding] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', price: '', stock: '', image_url: '',
    skin_type: 'All', product_type: 'Soin de visage', category: 'Treat',
    ingredients: '', how_to_use: '', best_seller: false
  });
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async () => {
    const res = await axios.get(`${API}/products`, { withCredentials: true });
    setProducts(res.data);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const saveEdit = async (productId) => {
    const update = {};
    if (editForm.price !== '') update.price = parseFloat(editForm.price);
    if (editForm.stock !== '') update.stock = parseInt(editForm.stock);
    await axios.put(`${API}/products/${productId}`, update, { withCredentials: true });
    setEditing(null);
    fetchProducts();
  };

  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.price) return;
    setSaving(true);
    try {
      const payload = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        stock: parseInt(newProduct.stock) || 0,
        ingredients: newProduct.ingredients ? newProduct.ingredients.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await axios.post(`${API}/products`, payload, { withCredentials: true });
      setAdding(false);
      setNewProduct({ name: '', description: '', price: '', stock: '', image_url: '', skin_type: 'All', product_type: 'Soin de visage', category: 'Treat', ingredients: '', how_to_use: '', best_seller: false });
      fetchProducts();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleDelete = async (productId) => {
    await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
    fetchProducts();
  };

  const filtered = products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()));
  const setNew = (k, v) => setNewProduct({...newProduct, [k]: v});

  return (
    <div data-testid="inventory-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Inventaire</h2>
          <p className="text-xs text-white/30 mt-1">{products.length} produits</p>
        </div>
        <div className="flex items-center gap-3">
          {!adding && (
            <button data-testid="add-product-btn" onClick={() => setAdding(true)} className="text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors">
              + Ajouter Produit
            </button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              data-testid="inventory-search"
              type="text"
              placeholder="Rechercher..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-white/5 border border-white/10 text-white text-sm pl-9 pr-4 py-2 outline-none focus:border-white/30 transition-colors w-48"
            />
          </div>
        </div>
      </div>

      {/* Add Product Form */}
      {adding && (
        <div data-testid="add-product-form" className="dash-card p-6 mb-8">
          <h3 className="text-sm text-white font-semibold mb-5">Nouveau Produit</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Nom *</label>
              <input data-testid="new-product-name" type="text" value={newProduct.name} onChange={e => setNew('name', e.target.value)} placeholder="Nom du produit" className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Prix (FCFA) *</label>
              <input data-testid="new-product-price" type="number" value={newProduct.price} onChange={e => setNew('price', e.target.value)} placeholder="0" className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Stock</label>
              <input data-testid="new-product-stock" type="number" value={newProduct.stock} onChange={e => setNew('stock', e.target.value)} placeholder="0" className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">URL Photo</label>
              <input data-testid="new-product-image" type="text" value={newProduct.image_url} onChange={e => setNew('image_url', e.target.value)} placeholder="https://..." className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30" />
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Cat&eacute;gorie</label>
              <select data-testid="new-product-type" value={newProduct.product_type} onChange={e => setNew('product_type', e.target.value)} className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30">
                <option value="Soin de visage">Soin de visage</option>
                <option value="Soin de corps">Soin de corps</option>
                <option value="Compl&eacute;ment alimentaire">Compl&eacute;ment alimentaire</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Type de peau</label>
              <select data-testid="new-product-skin" value={newProduct.skin_type} onChange={e => setNew('skin_type', e.target.value)} className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30">
                <option value="All">Tous</option>
                <option value="Oily">Grasse</option>
                <option value="Dry">S&egrave;che</option>
                <option value="Sensitive">Sensible</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Description</label>
              <textarea data-testid="new-product-desc" value={newProduct.description} onChange={e => setNew('description', e.target.value)} rows={2} placeholder="Description du produit..." className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30 resize-none" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Ingr&eacute;dients (s&eacute;par&eacute;s par des virgules)</label>
              <input data-testid="new-product-ingredients" type="text" value={newProduct.ingredients} onChange={e => setNew('ingredients', e.target.value)} placeholder="Vitamine C, Acide Hyaluronique, Niacinamide" className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-white/40 uppercase tracking-widest block mb-1">Mode d'emploi</label>
              <textarea data-testid="new-product-howto" value={newProduct.how_to_use} onChange={e => setNew('how_to_use', e.target.value)} rows={2} placeholder="Comment utiliser ce produit..." className="bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 w-full outline-none focus:border-white/30 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button data-testid="save-new-product-btn" onClick={handleAddProduct} disabled={saving || !newProduct.name || !newProduct.price} className="text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-30">
              {saving ? 'Ajout...' : 'Ajouter'}
            </button>
            <button onClick={() => setAdding(false)} className="text-xs text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest px-4 py-2.5">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(product => (
          <div key={product.product_id} data-testid={`inventory-item-${product.product_id}`} className="dash-card p-4 flex items-center gap-4">
            <div className="w-14 h-14 bg-zinc-800 flex-shrink-0">
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover opacity-80" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm text-white font-semibold truncate">{product.name}</h3>
                {product.stock < 10 && <span className="low-stock-dot" title="Low stock" />}
              </div>
              <p className="text-xs text-white/40">{product.skin_type} &middot; {product.category}</p>
            </div>

            {editing === product.product_id ? (
              <div className="flex items-center gap-3">
                <input
                  data-testid={`edit-price-${product.product_id}`}
                  type="number"
                  step="0.01"
                  placeholder="Price"
                  value={editForm.price}
                  onChange={e => setEditForm({...editForm, price: e.target.value})}
                  className="bg-white/5 border border-white/10 text-white text-sm px-3 py-1.5 w-24 outline-none"
                />
                <input
                  data-testid={`edit-stock-${product.product_id}`}
                  type="number"
                  placeholder="Stock"
                  value={editForm.stock}
                  onChange={e => setEditForm({...editForm, stock: e.target.value})}
                  className="bg-white/5 border border-white/10 text-white text-sm px-3 py-1.5 w-20 outline-none"
                />
                <button
                  data-testid={`save-edit-${product.product_id}`}
                  onClick={() => saveEdit(product.product_id)}
                  className="text-xs uppercase tracking-widest bg-white text-black px-4 py-1.5 hover:bg-zinc-200 transition-colors"
                >
                  Sauver
                </button>
                <button onClick={() => setEditing(null)} className="text-xs text-white/30 hover:text-white/60">Annuler</button>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <p className="text-sm text-white font-semibold">{formatFCFA(product.price)}</p>
                  <p className={`text-xs ${product.stock < 10 ? 'text-white/40' : 'text-white/40'}`} style={product.stock < 10 ? { color: '#B80000' } : {}}>
                    {product.stock} en stock
                  </p>
                </div>
                <button
                  data-testid={`edit-btn-${product.product_id}`}
                  onClick={() => {
                    setEditing(product.product_id);
                    setEditForm({ price: product.price.toString(), stock: product.stock.toString() });
                  }}
                  className="text-xs text-white/30 hover:text-white/60 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  data-testid={`delete-btn-${product.product_id}`}
                  onClick={() => { if (window.confirm('Supprimer ce produit ?')) handleDelete(product.product_id); }}
                  className="text-xs text-white/15 hover:text-red-400 transition-colors ml-2"
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [trackingInput, setTrackingInput] = useState({}); // { order_id: "TRACK..." }
  const [sendingStatus, setSendingStatus] = useState({}); // { order_id: bool }
  const [showExport, setShowExport] = useState(false);
  const [exportFilters, setExportFilters] = useState({ start_date: '', end_date: '', status: 'all', payment_status: 'all' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('Processing');
  const [bulkTracking, setBulkTracking] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    const res = await axios.get(`${API}/orders`, { withCredentials: true });
    setOrders(res.data);
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, status) => {
    setSendingStatus(s => ({ ...s, [orderId]: true }));
    try {
      const payload = status === 'Shipped' && trackingInput[orderId]
        ? `Shipped:${trackingInput[orderId].trim()}`
        : status;
      await axios.put(`${API}/orders/${orderId}/status`, { status: payload }, { withCredentials: true });
      await fetchOrders();
    } finally {
      setSendingStatus(s => ({ ...s, [orderId]: false }));
    }
  };

  const downloadExport = async () => {
    const params = new URLSearchParams();
    if (exportFilters.start_date) params.append('start_date', exportFilters.start_date);
    if (exportFilters.end_date) params.append('end_date', exportFilters.end_date);
    if (exportFilters.status && exportFilters.status !== 'all') params.append('status', exportFilters.status);
    if (exportFilters.payment_status && exportFilters.payment_status !== 'all') params.append('payment_status', exportFilters.payment_status);

    const res = await axios.get(`${API}/admin/orders/export?${params.toString()}`, {
      withCredentials: true,
      responseType: 'blob',
    });
    const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `balmart_commandes_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    setShowExport(false);
  };

  const statuses = ['Pending', 'Processing', 'Shipped', 'Delivered'];
  const statusLabels = { Pending: 'En attente', Processing: 'En préparation', Shipped: 'Expédiée', Delivered: 'Livrée' };
  const statusIndex = (s) => statuses.indexOf(s);

  return (
    <div data-testid="orders-panel">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Commandes</h2>
          <p className="text-xs text-white/30 mt-1">Un email est automatiquement envoyé au client lors du passage à "Expédiée" ou "Livrée"</p>
        </div>
        <button
          data-testid="export-csv-btn"
          onClick={() => setShowExport(true)}
          className="px-5 py-3 text-xs uppercase tracking-[0.2em] font-bold border transition-colors"
          style={{ borderColor: '#B80000', color: '#B80000' }}
        >
          Exporter CSV
        </button>
      </div>

      {showExport && (
        <div data-testid="export-modal" className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowExport(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 max-w-md w-full p-8" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-2xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Exporter les commandes</h3>
            <p className="text-xs text-white/40 mb-6">Format CSV compatible Excel (UTF-8 avec BOM)</p>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Du</label>
                  <input
                    data-testid="export-start-date"
                    type="date"
                    value={exportFilters.start_date}
                    onChange={e => setExportFilters({ ...exportFilters, start_date: e.target.value })}
                    className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Au</label>
                  <input
                    data-testid="export-end-date"
                    type="date"
                    value={exportFilters.end_date}
                    onChange={e => setExportFilters({ ...exportFilters, end_date: e.target.value })}
                    className="w-full bg-transparent border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Statut commande</label>
                <select
                  data-testid="export-status"
                  value={exportFilters.status}
                  onChange={e => setExportFilters({ ...exportFilters, status: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
                >
                  <option value="all">Toutes</option>
                  <option value="Pending">En attente</option>
                  <option value="Processing">En préparation</option>
                  <option value="Shipped">Expédiées</option>
                  <option value="Delivered">Livrées</option>
                  <option value="Cancelled">Annulées</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40 block mb-2">Statut paiement</label>
                <select
                  data-testid="export-payment"
                  value={exportFilters.payment_status}
                  onChange={e => setExportFilters({ ...exportFilters, payment_status: e.target.value })}
                  className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-3 py-2 text-sm text-white"
                >
                  <option value="all">Tous</option>
                  <option value="paid">Payés</option>
                  <option value="awaiting_payment">En attente</option>
                  <option value="failed">Échoués</option>
                  <option value="cancelled">Annulés</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                data-testid="confirm-export"
                onClick={downloadExport}
                className="flex-1 py-3 text-xs uppercase tracking-[0.2em] font-bold"
                style={{ backgroundColor: '#B80000', color: '#fff' }}
              >
                Télécharger le CSV
              </button>
              <button
                onClick={() => setShowExport(false)}
                className="px-5 py-3 text-xs uppercase tracking-[0.2em] text-white/50 hover:text-white"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="dash-card p-8 text-center">
          <p className="text-white/30 text-sm">Aucune commande pour le moment</p>
        </div>
      ) : (
        <>
          {/* Bulk actions bar */}
          <div data-testid="bulk-actions-bar" className="mb-4 dash-card p-4 flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 cursor-pointer">
              <input
                data-testid="select-all-orders"
                type="checkbox"
                checked={selectedIds.size > 0 && selectedIds.size === orders.length}
                onChange={e => {
                  if (e.target.checked) setSelectedIds(new Set(orders.map(o => o.order_id)));
                  else setSelectedIds(new Set());
                }}
                className="w-4 h-4 accent-[#B80000]"
              />
              Tout sélectionner
            </label>
            <span className="text-xs text-white/40">
              {selectedIds.size} sélectionnée{selectedIds.size > 1 ? 's' : ''}
            </span>
            {selectedIds.size > 0 && (
              <>
                <div className="h-5 w-px bg-white/10" />
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger data-testid="bulk-status-select" className="w-40 bg-white/5 border-white/10 text-white text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/10">
                    <SelectItem value="Processing" className="text-white text-xs">En préparation</SelectItem>
                    <SelectItem value="Shipped" className="text-white text-xs">Expédiée</SelectItem>
                    <SelectItem value="Delivered" className="text-white text-xs">Livrée</SelectItem>
                    <SelectItem value="Cancelled" className="text-white text-xs">Annulée</SelectItem>
                  </SelectContent>
                </Select>
                {bulkStatus === 'Shipped' && (
                  <input
                    data-testid="bulk-tracking-input"
                    value={bulkTracking}
                    onChange={e => setBulkTracking(e.target.value)}
                    placeholder="N° de suivi (optionnel)"
                    className="bg-white/5 border border-white/10 text-white text-xs px-3 h-8 w-48 placeholder-white/30"
                  />
                )}
                <button
                  data-testid="bulk-apply-btn"
                  disabled={bulkLoading}
                  onClick={async () => {
                    setBulkLoading(true);
                    try {
                      await axios.put(`${API}/orders/bulk-status`, {
                        order_ids: Array.from(selectedIds),
                        status: bulkStatus,
                        tracking_number: bulkStatus === 'Shipped' ? (bulkTracking.trim() || null) : null,
                      }, { withCredentials: true });
                      setSelectedIds(new Set());
                      setBulkTracking('');
                      await fetchOrders();
                    } finally {
                      setBulkLoading(false);
                    }
                  }}
                  className="ml-auto px-4 h-8 text-xs uppercase tracking-widest font-bold disabled:opacity-50"
                  style={{ backgroundColor: '#B80000', color: '#fff' }}
                >
                  {bulkLoading ? 'Application…' : `Appliquer (${selectedIds.size})`}
                </button>
              </>
            )}
          </div>

          <div className="space-y-3">
          {orders.map(order => {
            const idx = statusIndex(order.status || 'Pending');
            const selected = selectedIds.has(order.order_id);
            return (
              <div
                key={order.order_id}
                data-testid={`order-${order.order_id}`}
                className={`dash-card p-5 ${selected ? 'ring-1 ring-[#B80000]' : ''}`}
              >
                <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-start gap-3">
                    <input
                      data-testid={`order-select-${order.order_id}`}
                      type="checkbox"
                      checked={selected}
                      onChange={e => {
                        setSelectedIds(prev => {
                          const next = new Set(prev);
                          e.target.checked ? next.add(order.order_id) : next.delete(order.order_id);
                          return next;
                        });
                      }}
                      className="w-4 h-4 accent-[#B80000] mt-1 cursor-pointer"
                    />
                    <div>
                    <p className="text-sm text-white font-semibold">{order.customer_name}</p>
                    <p className="text-xs text-white/30">{order.customer_email}</p>
                    <p className="text-xs text-white/20 mt-1">#{order.order_id}
                      {order.payment_status === 'paid' && <span className="ml-2 text-green-400">· Payée</span>}
                      {order.payment_method === 'cod' && <span className="ml-2 text-yellow-400">· COD</span>}
                    </p>
                    {order.tracking_number && (
                      <p className="text-xs text-white/40 mt-1">
                        Suivi: <span className="font-mono text-white/70 tracking-wider">{order.tracking_number}</span>
                      </p>
                    )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg text-white font-light">{formatFCFA(order.total)}</p>
                    <p className="text-xs text-white/30">{order.items?.length || 0} articles</p>
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex items-center mt-4 mb-3">
                  {statuses.map((s, i) => (
                    <div key={s} className="flex-1 flex items-center" style={{ maxWidth: i === statuses.length - 1 ? '60px' : 'none' }}>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 transition-colors"
                        style={{
                          backgroundColor: i <= idx ? '#B80000' : 'rgba(255,255,255,0.08)',
                          color: i <= idx ? '#fff' : 'rgba(255,255,255,0.3)',
                        }}
                      >
                        {i + 1}
                      </div>
                      {i < statuses.length - 1 && (
                        <div
                          className="flex-1 h-px mx-2"
                          style={{ backgroundColor: i < idx ? '#B80000' : 'rgba(255,255,255,0.08)' }}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-wrap mt-4">
                  <span className="text-xs text-white/30 uppercase tracking-widest mr-2">Mettre à jour:</span>
                  {statuses.map(s => {
                    const disabled = sendingStatus[order.order_id] || order.status === s;
                    const needsTracking = s === 'Shipped' && !trackingInput[order.order_id] && order.status !== 'Shipped';
                    return (
                      <button
                        key={s}
                        data-testid={`order-status-${order.order_id}-${s.toLowerCase()}`}
                        onClick={() => updateStatus(order.order_id, s)}
                        disabled={disabled}
                        className="text-xs px-3 py-1.5 transition-colors"
                        style={{
                          backgroundColor: order.status === s ? '#fff' : 'rgba(255,255,255,0.05)',
                          color: order.status === s ? '#000' : 'rgba(255,255,255,0.5)',
                          opacity: disabled ? 0.6 : 1,
                        }}
                      >
                        {statusLabels[s]}
                        {needsTracking && <span className="text-[9px] ml-1 opacity-60">(+ suivi)</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Tracking input when moving to Shipped */}
                {order.status !== 'Shipped' && order.status !== 'Delivered' && (
                  <div className="mt-3">
                    <input
                      type="text"
                      data-testid={`tracking-input-${order.order_id}`}
                      value={trackingInput[order.order_id] || ''}
                      onChange={e => setTrackingInput(s => ({ ...s, [order.order_id]: e.target.value }))}
                      placeholder="Numéro de suivi (optionnel) — ex: DHL123456SN"
                      className="w-full bg-transparent border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-xs text-white tracking-wider"
                    />
                    <p className="text-[10px] text-white/20 mt-1">
                      Lors du passage à "Expédiée", ce numéro sera envoyé dans l'email de notification au client.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}

function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('daily'); // daily | weekly | monthly

  useEffect(() => {
    axios.get(`${API}/analytics`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(console.error);
  }, []);

  if (!data) return <div className="text-white/30 text-sm">Chargement des analyses...</div>;

  const chartData = {
    daily: data.daily_sales.map(d => ({ label: d.date.slice(5), orders: d.orders, revenue: d.revenue })),
    weekly: data.weekly_sales.map(d => ({ label: d.week.replace(/^\d+-/, ''), orders: d.orders, revenue: d.revenue })),
    monthly: data.monthly_sales.map(d => ({ label: d.month, orders: d.orders, revenue: d.revenue })),
  }[period];

  const stats = [
    { label: 'CA', value: formatFCFA(data.total_revenue), color: '#B80000' },
    { label: 'Commandes', value: data.total_orders, color: '#fff' },
    { label: 'Panier moyen', value: formatFCFA(data.avg_basket), color: '#fff' },
    { label: 'Clients', value: data.total_customers, color: '#fff' },
    { label: 'Conversion', value: `${data.conversion_rate}%`, color: data.conversion_rate > 50 ? '#4ade80' : '#fbbf24' },
    { label: 'Abandons', value: data.abandoned_carts, color: '#fbbf24' },
  ];

  const statusStats = [
    { label: 'En attente', value: data.pending_orders, color: 'text-white/60' },
    { label: 'En préparation', value: data.processing_orders, color: 'text-yellow-400' },
    { label: 'Expédiées', value: data.shipped_orders, color: 'text-blue-400' },
    { label: 'Livrées', value: data.delivered_orders, color: 'text-green-400' },
    { label: 'Annulées', value: data.cancelled_orders, color: 'text-red-400' },
  ];

  return (
    <div data-testid="analytics-panel">
      <h2 className="font-serif text-3xl text-white font-light mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Analytique</h2>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {stats.map(s => (
          <div key={s.label} className="dash-card p-5">
            <p className="text-xs text-white/30 uppercase tracking-widest mb-2">{s.label}</p>
            <p className="text-xl font-light" style={{ color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue Chart with period selector */}
      <div className="dash-card p-6 mb-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h3 className="text-xs text-white/30 uppercase tracking-widest">Revenus {period === 'daily' ? 'Journaliers (30j)' : period === 'weekly' ? 'Hebdomadaires (12 sem.)' : 'Mensuels (12 mois)'}</h3>
          <div className="flex gap-1">
            {[['daily', 'Jour'], ['weekly', 'Semaine'], ['monthly', 'Mois']].map(([k, label]) => (
              <button
                key={k}
                data-testid={`period-${k}`}
                onClick={() => setPeriod(k)}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 transition-colors"
                style={{
                  backgroundColor: period === k ? '#B80000' : 'rgba(255,255,255,0.05)',
                  color: period === k ? '#fff' : 'rgba(255,255,255,0.5)'
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-center text-white/30 py-12">Aucune donnée sur cette période</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
              <YAxis stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 12 }}
                labelStyle={{ color: 'rgba(255,255,255,0.5)' }}
                formatter={(value, name) => name === 'revenue' ? [`${Math.round(value).toLocaleString('fr-FR')} FCFA`, 'Revenus'] : [value, 'Commandes']}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="revenue" name="Revenus" stroke="#B80000" strokeWidth={2} dot={{ fill: '#B80000', r: 3 }} activeDot={{ r: 6 }} />
              <Line type="monotone" dataKey="orders" name="Commandes" stroke="#fff" strokeWidth={1.5} dot={{ fill: '#fff', r: 2 }} yAxisId={0} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Products */}
        <div className="dash-card p-6">
          <h3 className="text-xs text-white/30 uppercase tracking-widest mb-4">Top 10 Produits</h3>
          {data.top_products?.length ? (
            <div className="space-y-2">
              {data.top_products.map((p, i) => (
                <div key={p.product_id} data-testid={`top-product-${i}`} className="flex items-center gap-3 p-2 hover:bg-white/5 transition-colors">
                  <span className="text-xs font-bold w-6" style={{ color: i < 3 ? '#B80000' : 'rgba(255,255,255,0.3)' }}>
                    #{i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{p.name}</p>
                    <p className="text-[10px] text-white/30">{p.units_sold} unité{p.units_sold > 1 ? 's' : ''} vendue{p.units_sold > 1 ? 's' : ''}</p>
                  </div>
                  <p className="text-xs text-white/70 font-semibold whitespace-nowrap">{Math.round(p.revenue).toLocaleString('fr-FR')} FCFA</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-white/30 py-8 text-sm">Aucune vente</p>
          )}
        </div>

        {/* Order Status Breakdown */}
        <div className="dash-card p-6">
          <h3 className="text-xs text-white/30 uppercase tracking-widest mb-4">Répartition des statuts</h3>
          <div className="space-y-3">
            {statusStats.map(s => {
              const pct = data.total_orders > 0 ? Math.round((s.value / data.total_orders) * 100) : 0;
              return (
                <div key={s.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm ${s.color}`}>{s.label}</span>
                    <span className="text-xs text-white/50">{s.value} · {pct}%</span>
                  </div>
                  <div className="h-1 bg-white/5 overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#B80000' }} />
                  </div>
                </div>
              );
            })}
          </div>

          {data.top_promos?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/5">
              <h4 className="text-xs text-white/30 uppercase tracking-widest mb-3">Top codes promo</h4>
              <div className="space-y-2">
                {data.top_promos.map(p => (
                  <div key={p.code} className="flex items-center justify-between">
                    <span className="text-xs text-white tracking-wider" style={{ fontFamily: 'Georgia, serif' }}>{p.code}</span>
                    <span className="text-xs text-white/40">{p.times_used} usages · -{p.discount_percent}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Low Stock Alerts */}
      {data.low_stock_products.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xs text-white/30 uppercase tracking-widest mb-4 flex items-center gap-2">
            <AlertCircle size={14} style={{ color: '#B80000' }} /> Alertes Stock Bas ({data.low_stock_products.length})
          </h3>
          <div className="space-y-2">
            {data.low_stock_products.map(p => (
              <div key={p.product_id} data-testid={`low-stock-${p.product_id}`} className="dash-card p-4 flex items-center justify-between" style={{ borderLeft: '2px solid #B80000' }}>
                <div className="flex items-center gap-3">
                  <span className="low-stock-dot" />
                  <span className="text-sm text-white">{p.name}</span>
                </div>
                <span className="text-sm font-semibold" style={{ color: '#B80000' }}>{p.stock} unit&eacute;s</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InboxPanel() {
  const [inquiries, setInquiries] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState('');

  const fetchInquiries = useCallback(async () => {
    const res = await axios.get(`${API}/inquiries`, { withCredentials: true });
    setInquiries(res.data);
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const sendReply = async (inquiryId) => {
    await axios.put(`${API}/inquiries/${inquiryId}/reply`, { reply: replyText }, { withCredentials: true });
    setReplyTo(null);
    setReplyText('');
    fetchInquiries();
  };

  return (
    <div data-testid="inbox-panel">
      <h2 className="font-serif text-3xl text-white font-light mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Customer Inbox</h2>

      {inquiries.length === 0 ? (
        <div className="dash-card p-8 text-center">
          <p className="text-white/30 text-sm">No inquiries yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map(inq => (
            <div key={inq.inquiry_id} data-testid={`inquiry-${inq.inquiry_id}`} className="dash-card p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm text-white font-semibold">{inq.name}</p>
                  <p className="text-xs text-white/30">{inq.email}</p>
                </div>
                <span className={`text-xs px-2 py-1 ${inq.status === 'Replied' ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/30'}`}>
                  {inq.status}
                </span>
              </div>
              <p className="text-xs text-white/50 font-semibold mb-1">{inq.subject}</p>
              <p className="text-sm text-white/60">{inq.message}</p>

              {inq.reply && (
                <div className="mt-3 pl-4 border-l border-white/10">
                  <p className="text-xs text-white/30 mb-1">Votre r&eacute;ponse:</p>
                  <p className="text-sm text-white/50">{inq.reply}</p>
                </div>
              )}

              {replyTo === inq.inquiry_id ? (
                <div className="mt-3 flex gap-2">
                  <input
                    data-testid={`reply-input-${inq.inquiry_id}`}
                    type="text"
                    placeholder="Tapez votre r&eacute;ponse..."
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3 py-2 outline-none focus:border-white/30"
                  />
                  <button
                    data-testid={`send-reply-${inq.inquiry_id}`}
                    onClick={() => sendReply(inq.inquiry_id)}
                    className="text-xs uppercase tracking-widest bg-white text-black px-4 py-2 hover:bg-zinc-200 transition-colors"
                  >
                    Envoyer
                  </button>
                </div>
              ) : (
                !inq.reply && (
                  <button
                    data-testid={`reply-btn-${inq.inquiry_id}`}
                    onClick={() => setReplyTo(inq.inquiry_id)}
                    className="mt-3 text-xs text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest"
                  >
                    R&eacute;pondre
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/customers`, { withCredentials: true })
      .then(res => { setCustomers(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = customers.filter(c =>
    `${c.first_name} ${c.last_name} ${c.email} ${c.phone} ${c.address}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="customers-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Clients</h2>
          <p className="text-xs text-white/30 mt-1">{customers.length} client{customers.length !== 1 ? 's' : ''} inscrits</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="customers-search"
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-white/5 border border-white/10 text-white text-sm pl-9 pr-4 py-2 outline-none focus:border-white/30 transition-colors w-64"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12"><p className="text-white/30 text-sm">Chargement...</p></div>
      ) : filtered.length === 0 ? (
        <div className="dash-card p-8 text-center">
          <p className="text-white/30 text-sm">{search ? 'Aucun client trouv\u00e9' : 'Aucun client inscrit'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs text-white/30 uppercase tracking-widest">
            <div className="col-span-3">Nom</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">T&eacute;l&eacute;phone</div>
            <div className="col-span-3">Adresse</div>
            <div className="col-span-1">Date</div>
          </div>
          {filtered.map(c => (
            <div key={c.customer_id} data-testid={`customer-row-${c.customer_id}`} className="dash-card grid grid-cols-12 gap-4 px-4 py-4 items-center">
              <div className="col-span-3">
                <p className="text-sm text-white font-semibold truncate">{c.first_name} {c.last_name}</p>
              </div>
              <div className="col-span-3">
                <p className="text-sm text-white/60 truncate">{c.email}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-white/60">{c.phone || '\u2014'}</p>
              </div>
              <div className="col-span-3">
                <p className="text-sm text-white/60 truncate">{c.address || '\u2014'}</p>
              </div>
              <div className="col-span-1">
                <p className="text-xs text-white/30">{new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function EmailCampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', recipient_type: 'all', recipient_ids: [] });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    const res = await axios.get(`${API}/admin/bulk-email`, { withCredentials: true });
    setCampaigns(res.data);
  }, []);

  const fetchCustomers = useCallback(async () => {
    const res = await axios.get(`${API}/admin/customers`, { withCredentials: true });
    setCustomers(res.data);
  }, []);

  useEffect(() => { fetchCampaigns(); fetchCustomers(); }, [fetchCampaigns, fetchCustomers]);

  const toggleRecipient = (id) => {
    setForm(prev => ({
      ...prev,
      recipient_ids: prev.recipient_ids.includes(id)
        ? prev.recipient_ids.filter(r => r !== id)
        : [...prev.recipient_ids, id]
    }));
  };

  const handleSend = async () => {
    if (!form.subject.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/bulk-email`, form, { withCredentials: true });
      setSent(true);
      setForm({ subject: '', message: '', recipient_type: 'all', recipient_ids: [] });
      fetchCampaigns();
      setTimeout(() => { setSent(false); setComposing(false); }, 2000);
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const viewDetail = async (campaignId) => {
    if (expandedCampaign === campaignId) { setExpandedCampaign(null); setCampaignDetail(null); return; }
    const res = await axios.get(`${API}/admin/bulk-email/${campaignId}`, { withCredentials: true });
    setCampaignDetail(res.data);
    setExpandedCampaign(campaignId);
  };

  return (
    <div data-testid="emails-panel">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Campagnes Email</h2>
          <p className="text-xs text-white/30 mt-1">{campaigns.length} campagne{campaigns.length !== 1 ? 's' : ''} envoy&eacute;e{campaigns.length !== 1 ? 's' : ''}</p>
        </div>
        {!composing && (
          <button data-testid="new-campaign-btn" onClick={() => setComposing(true)} className="text-xs uppercase tracking-widest bg-white text-black px-5 py-2.5 hover:bg-zinc-200 transition-colors flex items-center gap-2">
            <Mail size={14} /> Nouvelle Campagne
          </button>
        )}
      </div>

      {/* Compose */}
      {composing && (
        <div data-testid="compose-campaign" className="dash-card p-6 mb-8">
          <h3 className="text-sm text-white font-semibold mb-5">Nouveau Email en Masse</h3>

          {sent ? (
            <div className="text-center py-6">
              <p className="text-green-400 text-sm">Campagne envoy&eacute;e avec succ&egrave;s !</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Recipients */}
              <div>
                <label className="text-xs text-white/40 uppercase tracking-widest block mb-2">Destinataires</label>
                <div className="flex gap-2 mb-3">
                  <button
                    data-testid="recipients-all"
                    onClick={() => setForm({...form, recipient_type: 'all', recipient_ids: []})}
                    className={`text-xs px-4 py-2 transition-colors ${form.recipient_type === 'all' ? 'bg-white text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                  >
                    Tous les clients ({customers.length})
                  </button>
                  <button
                    data-testid="recipients-selected"
                    onClick={() => setForm({...form, recipient_type: 'selected'})}
                    className={`text-xs px-4 py-2 transition-colors ${form.recipient_type === 'selected' ? 'bg-white text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                  >
                    S&eacute;lectionner
                  </button>
                </div>

                {form.recipient_type === 'selected' && (
                  <div className="max-h-40 overflow-y-auto space-y-1 bg-white/5 p-3">
                    {customers.map(c => (
                      <label key={c.customer_id} className="flex items-center gap-3 py-1.5 px-2 hover:bg-white/5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.recipient_ids.includes(c.customer_id)}
                          onChange={() => toggleRecipient(c.customer_id)}
                          className="accent-white"
                        />
                        <span className="text-sm text-white/70">{c.first_name} {c.last_name}</span>
                        <span className="text-xs text-white/30">{c.email}</span>
                      </label>
                    ))}
                    {form.recipient_ids.length > 0 && (
                      <p className="text-xs text-white/30 mt-2">{form.recipient_ids.length} client{form.recipient_ids.length > 1 ? 's' : ''} s&eacute;lectionn&eacute;{form.recipient_ids.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Subject */}
              <div>
                <label className="text-xs text-white/40 uppercase tracking-widest block mb-2">Sujet</label>
                <input
                  data-testid="campaign-subject"
                  type="text"
                  value={form.subject}
                  onChange={e => setForm({...form, subject: e.target.value})}
                  placeholder="Ex: Nouvelle collection disponible !"
                  className="bg-white/5 border border-white/10 text-white text-sm px-4 py-3 w-full outline-none focus:border-white/30 transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <label className="text-xs text-white/40 uppercase tracking-widest block mb-2">Message</label>
                <textarea
                  data-testid="campaign-message"
                  value={form.message}
                  onChange={e => setForm({...form, message: e.target.value})}
                  placeholder="Votre message pour les clients..."
                  rows={5}
                  className="bg-white/5 border border-white/10 text-white text-sm px-4 py-3 w-full outline-none focus:border-white/30 transition-colors resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  data-testid="send-campaign-btn"
                  onClick={handleSend}
                  disabled={sending || !form.subject.trim() || !form.message.trim() || (form.recipient_type === 'selected' && form.recipient_ids.length === 0)}
                  className="text-xs uppercase tracking-widest bg-white text-black px-6 py-2.5 hover:bg-zinc-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Mail size={12} /> {sending ? 'Envoi...' : 'Envoyer'}
                </button>
                <button onClick={() => { setComposing(false); setSent(false); }} className="text-xs text-white/30 hover:text-white/60 transition-colors uppercase tracking-widest px-4 py-2.5">
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaigns History */}
      {campaigns.length === 0 && !composing ? (
        <div className="dash-card p-8 text-center">
          <Mail size={32} strokeWidth={1} className="text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">Aucune campagne envoy&eacute;e</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(camp => (
            <div key={camp.campaign_id} className="dash-card">
              <button
                data-testid={`campaign-${camp.campaign_id}`}
                onClick={() => viewDetail(camp.campaign_id)}
                className="w-full p-5 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-semibold truncate">{camp.subject}</p>
                  <p className="text-xs text-white/30 mt-1">
                    {camp.recipient_count} destinataire{camp.recipient_count > 1 ? 's' : ''}
                    {typeof camp.delivered_count === 'number' && (
                      <>
                        {' · '}<span className="text-green-400/70">{camp.delivered_count} livr&eacute;{camp.delivered_count > 1 ? 's' : ''}</span>
                        {camp.failed_count > 0 && (
                          <> {' · '}<span className="text-red-400/70">{camp.failed_count} &eacute;chec{camp.failed_count > 1 ? 's' : ''}</span></>
                        )}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 ${camp.sent_via === 'resend' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {camp.sent_via === 'resend' ? 'Envoy\u00e9' : 'Non envoy\u00e9'}
                  </span>
                  <span className="text-xs text-white/20">{new Date(camp.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </button>

              {expandedCampaign === camp.campaign_id && campaignDetail && (
                <div className="border-t border-white/5 p-5 bg-white/[0.02]">
                  <p className="text-xs text-white/30 uppercase tracking-widest mb-2">Message</p>
                  <p className="text-sm text-white/60 whitespace-pre-wrap mb-4">{campaignDetail.message}</p>
                  <p className="text-xs text-white/30 uppercase tracking-widest mb-2">Destinataires ({campaignDetail.recipients?.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {campaignDetail.recipients?.map((r, i) => (
                      <span key={i} className="text-xs bg-white/5 text-white/40 px-2 py-1">{r.email}</span>
                    ))}
                  </div>
                  <p className="text-xs text-white/50 mt-4 italic">
                    {campaignDetail.sent_via === 'resend'
                      ? `\u2713 Envoy\u00e9 via Resend \u2014 ${campaignDetail.delivered_count || 0} livr\u00e9${(campaignDetail.delivered_count || 0) > 1 ? 's' : ''}${campaignDetail.failed_count ? `, ${campaignDetail.failed_count} \u00e9chec(s)` : ''}`
                      : campaignDetail.sent_via}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewsPanel() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, 1, 2, 3, 4, 5

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
      setReviews(res.data || []);
    } catch {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, { withCredentials: true });
      setReviews(prev => prev.filter(r => r.review_id !== reviewId));
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const filtered = filter === 'all' ? reviews : reviews.filter(r => r.rating === parseInt(filter));
  const avg = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : '-';

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };

  return (
    <div data-testid="reviews-panel" className="space-y-8">
      <div>
        <h1 className="font-serif text-3xl sm:text-4xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Modération des Avis
        </h1>
        <p className="text-sm text-white/40">Supervisez et modérez les avis clients</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Total</p>
          <p className="text-2xl font-light text-white mt-1">{reviews.length}</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">Note moyenne</p>
          <p className="text-2xl font-light text-white mt-1">{avg}<span className="text-sm text-white/40">/5</span></p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">5 étoiles</p>
          <p className="text-2xl font-light text-white mt-1">{reviews.filter(r => r.rating === 5).length}</p>
        </div>
        <div className="bg-white/5 border border-white/10 p-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40">≤ 2 étoiles</p>
          <p className="text-2xl font-light mt-1" style={{ color: '#B80000' }}>{reviews.filter(r => r.rating <= 2).length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap" data-testid="reviews-filter-bar">
        {['all', '5', '4', '3', '2', '1'].map(f => (
          <button
            key={f}
            data-testid={`filter-rating-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-xs uppercase tracking-widest transition-colors ${filter === f ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
          >
            {f === 'all' ? 'Tous' : `${f} ★`}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-white/40 text-sm">Chargement...</p>
      ) : filtered.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="no-reviews-msg">Aucun avis correspondant</p>
      ) : (
        <div className="space-y-3">
          {filtered.map(review => (
            <div
              key={review.review_id}
              data-testid={`admin-review-${review.review_id}`}
              className="bg-white/5 border border-white/10 p-5 hover:bg-white/10 transition-colors"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold text-white">{review.reviewer_name}</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => (
                        <Star
                          key={s}
                          size={12}
                          fill={s <= review.rating ? '#B80000' : 'none'}
                          stroke={s <= review.rating ? '#B80000' : '#ffffff40'}
                          strokeWidth={1.5}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] uppercase tracking-widest text-white/30">{formatDate(review.created_at)}</span>
                  </div>
                  <p className="text-[11px] text-white/40 mt-1 truncate">
                    Produit: <span className="text-white/60">{review.product_name}</span>
                  </p>
                </div>
                <button
                  data-testid={`delete-review-${review.review_id}`}
                  onClick={() => handleDelete(review.review_id)}
                  className="text-xs uppercase tracking-widest px-3 py-1.5 border border-white/10 hover:border-[#B80000] hover:text-[#B80000] text-white/60 transition-colors flex-shrink-0"
                >
                  Supprimer
                </button>
              </div>
              <p className="text-sm text-white/70 leading-relaxed">{review.comment}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState('inventory');
  const [newOrderToast, setNewOrderToast] = useState(null);
  const lastSeenOrderRef = useRef(null);

  const passedUser = location.state?.user;
  const currentUser = user || passedUser;

  useEffect(() => {
    if (!loading && !currentUser) {
      navigate('/admin');
    }
  }, [loading, currentUser, navigate]);

  // Real-time order notifications (poll every 30s)
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;

    const playDing = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.15);
        g.gain.setValueAtTime(0.15, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        o.start(ctx.currentTime);
        o.stop(ctx.currentTime + 0.4);
      } catch {}
    };

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/admin/orders/count`, { withCredentials: true });
        if (cancelled) return;
        const latestId = r.data?.latest_paid_order_id;
        if (latestId && lastSeenOrderRef.current === null) {
          // First poll — establish baseline without notifying
          lastSeenOrderRef.current = latestId;
        } else if (latestId && latestId !== lastSeenOrderRef.current) {
          lastSeenOrderRef.current = latestId;
          setNewOrderToast({ order_id: latestId, at: r.data.latest_paid_order_at });
          playDing();
          setTimeout(() => setNewOrderToast(null), 8000);
        }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [currentUser]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading && !currentUser) {
    return <div className="dashboard-dark flex items-center justify-center min-h-screen"><p className="text-white/30 text-sm">Chargement...</p></div>;
  }

  if (!currentUser) return null;

  return (
    <div data-testid="dashboard-page" className="dashboard-dark flex">
      {/* New order toast */}
      {newOrderToast && (
        <div
          data-testid="new-order-toast"
          className="fixed top-6 right-6 z-[100] max-w-sm cursor-pointer"
          style={{ animation: 'slideInRight 0.4s ease-out' }}
          onClick={() => { setActive('orders'); setNewOrderToast(null); }}
        >
          <div className="bg-[#0A0A0A] border-l-4 shadow-2xl p-4 flex items-start gap-3" style={{ borderColor: '#B80000', boxShadow: '0 10px 40px rgba(184,0,0,0.3)' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 animate-pulse" style={{ backgroundColor: '#B80000' }}>
              <Bell size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">Nouvelle commande payée</p>
              <p className="text-xs text-white/50 mt-0.5 truncate">#{newOrderToast.order_id.replace('ord_', '').toUpperCase()}</p>
              <p className="text-[10px] text-white/30 mt-1 uppercase tracking-widest">Cliquer pour voir</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setNewOrderToast(null); }}
              className="text-white/30 hover:text-white"
            >
              ×
            </button>
          </div>
          <style>{`
            @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
          `}</style>
        </div>
      )}

      <DashSidebar active={active} setActive={setActive} user={currentUser} onLogout={handleLogout} />

      {/* Mobile Nav */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <h1 className="font-serif text-lg text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Balmart</h1>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="w-36 bg-white/5 border-white/10 text-white text-xs" data-testid="mobile-nav-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10">
            <SelectItem value="inventory" className="text-white text-xs">Inventaire</SelectItem>
            <SelectItem value="orders" className="text-white text-xs">Commandes</SelectItem>
            <SelectItem value="abandoned" className="text-white text-xs">Paniers abandonn&eacute;s</SelectItem>
            <SelectItem value="promos" className="text-white text-xs">Codes Promo</SelectItem>
            <SelectItem value="reviews" className="text-white text-xs">Avis</SelectItem>
            <SelectItem value="customers" className="text-white text-xs">Clients</SelectItem>
            <SelectItem value="emails" className="text-white text-xs">Emails</SelectItem>
            <SelectItem value="analytics" className="text-white text-xs">Analytique</SelectItem>
            <SelectItem value="inbox" className="text-white text-xs">Messages</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 p-6 sm:p-8 lg:p-12 pt-20 lg:pt-12 min-h-screen">
        {active === 'inventory' && <InventoryPanel />}
        {active === 'orders' && <OrdersPanel />}
        {active === 'payments' && <PaymentsMonitoringPanel />}
        {active === 'abandoned' && <AbandonedCartsPanel />}
        {active === 'promos' && <PromosPanel />}
        {active === 'reviews' && <ReviewsPanel />}
        {active === 'customers' && <CustomersPanel />}
        {active === 'emails' && <EmailCampaignsPanel />}
        {active === 'analytics' && <AnalyticsPanel />}
        {active === 'inbox' && <InboxPanel />}
      </div>
    </div>
  );
}
